"use client";

import { useEffect, useState, useMemo } from "react";
import { useRouter, usePathname } from "next/navigation";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import Loader from "@/Components/Loader/Loader";
const SECURE_ROUTES = ["/provider/dashboard", "/provider/compare", "/reps/compare", "/provider/doctors-meeting", "/provider/find-doctors", "/provider/reps-favourite", "/reps/dashboard", "/reps-meetings", "/my-products", "/reps-messages", "/message", "/favourites", "/profile-setting", "/provider/profile-setting", "/provider/security-system", "/onboarding", "/reps/account-setup", "/reps/work-details",
// Every /provider/(main)/* page renders the signed-in dashboard shell.
// Without an entry here a signed-out visitor is not redirected and gets
// that shell with an empty sidebar gutter and no header. Matching is
// exact, so each route has to be listed individually.
"/provider/emerging-pipeline", "/provider/favorites", "/provider/message", "/provider/doctors-reps", "/provider/noti", "/provider/chat-not-found"];

// The pages a signed-in rep is allowed on. These had drifted from the actual
// routes — the list said "/reps-messages", "/my-products", "/find-doctors"
// while the app serves "/reps/messages", "/reps/my-products",
// "/reps/find-doctors". Matched by prefix so nested pages such as
// /reps/my-products/add-manually are covered too.
const SECURE_REPS_ROUTES = ["/reps/dashboard", "/reps/compare", "/reps/favorites", "/reps/find-doctors", "/reps/meetings", "/reps/messages", "/reps/my-products", "/reps/account-setup", "/reps/work-details"];
const ONBOARDING_ROUTES = ["/onboarding", "/provider/profile-setting", "/provider/security-system", "/reps/account-setup", "/reps/work-details"];
const isRepsPath = pathname => SECURE_REPS_ROUTES.some(route => pathname === route || pathname.startsWith(route + "/"));
const UNSECURE_ROUTES = ["/reps/login", "/reps", "/reps/signup", "/provider/signup", "/change-password", "/provider/configure-setting", "/reps/work-details", "/reps/two-factor-verification", "/reps/security-system", "/reset-password"];

// Anything under these prefixes renders the signed-in shell, so it is
// protected by default. The exact-match list drifted out of date every time a
// page was added, and a signed-out visitor then got the dashboard chrome with
// an empty sidebar and no header instead of being sent to login.
const SECURE_PREFIXES = ["/provider/", "/reps/"];

// Sign-in / onboarding pages that live under those prefixes and must stay
// reachable without a token.
const PREFIX_EXEMPT = ["/provider", "/provider/login", "/provider/signup", "/provider/profile-setting", "/provider/security-system", "/provider/two-factor-verification", "/provider/configure-setting", "/reps", "/reps/login", "/reps/signup", "/reps/registration", "/reps/accept-invite", "/reps/work-details", "/reps/two-factor-verification", "/reps/security-system"];
const AUTH_PAGES = ["/login", "/signup", "/reps/login", "/reps/signup", "/provider/signup", "/provider/login", "/reset-password", "/change-password"];

/**
 * A path needs a token when it is named explicitly, or when it sits under a
 * protected prefix and is not a sign-in/onboarding exemption. SECURE_ROUTES
 * is checked first so an explicitly-secure page stays secure even when it
 * lives in an unauth folder group (e.g. /reps/account-setup).
 */
function isSecurePath(pathname) {
  if (!pathname) return false;
  if (SECURE_ROUTES.includes(pathname)) return true;
  if (PREFIX_EXEMPT.includes(pathname)) return false;
  if (UNSECURE_ROUTES.includes(pathname)) return false;
  if (AUTH_PAGES.includes(pathname)) return false;
  return SECURE_PREFIXES.some(prefix => pathname.startsWith(prefix));
}
function isProtectedRoute(pathname) {
  return isSecurePath(pathname) || UNSECURE_ROUTES.includes(pathname) || AUTH_PAGES.includes(pathname);
}
export default function SecureRoutes({
  children
}) {
  const router = useRouter();
  const pathname = usePathname();
  const token = Cookies.get("token");
  let isReps = Cookies.get("isReps");
  const users = useSelector(state => state?.userData?.userData?.user);
  const userStatus = useSelector(state_0 => state_0?.userData?.status);

  // Public routes (homepage, about, contact, product pages, etc.) never need
  // the loading gate — render children immediately so SSR content is visible.
  const needsGuard = useMemo(() => isProtectedRoute(pathname), [pathname]);
  const [loading, setLoading] = useState(needsGuard);
  useEffect(() => {
    // Public routes — nothing to guard
    if (!needsGuard) {
      setLoading(false);
      return;
    }
    handleRouteAccess();
  }, [token, pathname, router, users, needsGuard, userStatus]);
  const handleRouteAccess = () => {
    if (users && !isReps) {
      isReps = (users?.type === "reps").toString();
      Cookies.set("isReps", isReps);
    }

    // Redirect logged-in users away from auth pages
    if (token && users && users?.isAccountData && AUTH_PAGES.includes(pathname)) {
      if (isReps && isReps === "true") {
        router.push("/reps/dashboard");
      } else {
        router.push("/provider/dashboard");
      }
      return;
    }

    // No token and accessing a secure route
    if (!token && isSecurePath(pathname)) {
      window.location.href = "/login";
      return;
    }

    // No token and accessing an unsecured route
    if (!token && UNSECURE_ROUTES.includes(pathname)) {
      setLoading(false);
      return;
    }

    // Existing OTP screens can verify an owned session while its full profile
    // loads in the background; signup already returned its account identity.
    if (token && ["/provider/two-factor-verification", "/reps/two-factor-verification"].includes(pathname)) {
      setLoading(false);
      return;
    }
    if (token && !users) {
      // If the user-data API failed (expired/invalid token), clear the stale token
      // and allow the page to render instead of loading forever
      if (userStatus === "failed") {
        Cookies.remove("token");
        Cookies.remove("isReps");
        setLoading(false);
        return;
      }
      // Still loading user data, wait
      return;
    }

    // The shared setup page decides which persisted fields are still missing.
    // Let unfinished accounts reach it without the old isAccountData redirect
    // sending them back to an already completed profile or territory screen.
    if (token && users && ONBOARDING_ROUTES.includes(pathname) && ["physician", "reps"].includes(users.type)) {
      if (users.isVerifiedAccount !== true) {
        router.push(users.type === "reps" ? "/reps/two-factor-verification" : "/provider/two-factor-verification");
        return;
      }
      setLoading(false);
      return;
    }

    // Token exists and accessing a secure route with valid user account data
    if (token && isSecurePath(pathname) && users && users?.isAccountData) {
      if (isReps && isReps === "true" && !isRepsPath(pathname)) {
        router.push("/reps/dashboard");
      }
      if (isReps && isReps === "false" && isRepsPath(pathname)) {
        router.push("/");
        return;
      }
      setLoading(false);
      return;
    }

    // Token exists and accessing an unsecured route with valid user account data
    if (token && UNSECURE_ROUTES.includes(pathname) && users && users?.isAccountData) {
      if (isReps && isReps === "true") {
        if (!users?.auth_otp) {
          router.push("/reps/dashboard");
        } else if (users?.auth_otp) {
          router.push("/reps/two-factor-verification");
        }
      } else {
        if (users?.twoFactorStatus && !users?.auth_otp) {
          router.push("/provider/dashboard");
        } else if (users?.twoFactorStatus && users?.auth_otp) {
          router.push("/provider/two-factor-verification");
        }
      }
      return;
    } else if (token && UNSECURE_ROUTES.includes(pathname) && !users) {
      setLoading(false);
      return;
    }

    // Token exists and accessing a secure route with invalid user account data
    if (token && isSecurePath(pathname) && users && !users?.isAccountData && users.type !== "clinic") {
      if (users?.auth_otp && isReps === "true") {
        router.push("/reps/two-factor-verification");
        return;
      } else if (users?.auth_otp && isReps === "false") {
        router.push("/provider/two-factor-verification");
        return;
      }
      if (isReps && isReps === "false" && !users?.isAccountData) {
        router.push("/provider/profile-setting");
        return;
      }
      if (isReps && isReps === "true" && !users?.isAccountData && pathname === "/reps/account-setup") {
        setLoading(false);
        return;
      }
      if (isReps && isReps === "true" && !users?.isAccountData) {
        router.push("/reps/account-setup");
        return;
      }
    }
    if (token && isSecurePath(pathname) && users && !users?.isAccountData && users.type === "clinic") {
      if (users?.auth_otp && isReps === "false") {
        router.push("/provider/two-factor-verification");
        return;
      }
      if (pathname === "/provider/configure-setting") {
        setLoading(false);
        return;
      }
      router.push("/provider/configure-setting");
      return;
    }
    setLoading(false);
  };
  if (loading) {
    return <Loader />;
  }
  if (!token && isSecurePath(pathname)) {
    return <Loader />;
  }
  if (token && isSecurePath(pathname) && !users) {
    return <Loader />;
  }
  return <>{children}</>;
}